import { useEffect, useRef, useState } from "react";
import { Box, Flex, Loader, Text } from "@mantine/core";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  IconArrowLeft,
  IconFileDescription,
  IconFolder,
  IconSettings,
} from "@tabler/icons-react";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { EHR_OPTIONS } from "../../../utils/ehrOptions";
import { getProviderSettings, updateProviderSettings } from "../../../services/skypointProvider";
import useStyles from "./style";

const Toggle = ({ classes, cx, on, onClick }) => (
  <button
    type="button"
    className={cx(classes.toggle, { [classes.toggleOn]: on })}
    onClick={onClick}
    aria-pressed={on}
  />
);

const Segmented = ({ classes, cx, options, value, onChange }) => (
  <Box className={classes.segmented}>
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        className={cx(classes.segItem, { [classes.segItemOn]: value === opt.value })}
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </Box>
);

const ChipRow = ({ classes, cx, options, selected, onToggle }) => (
  <Box className={classes.chipRow}>
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        className={cx(classes.chip, { [classes.chipOn]: selected.includes(opt.value) })}
        onClick={() => onToggle(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </Box>
);

const SETTINGS_SECTIONS = [
  { key: "general", label: "General", icon: IconSettings },
  { key: "external", label: "External Providers", icon: IconFileDescription },
  { key: "documents", label: "Documents", icon: IconFolder },
];

const PROVIDER_TYPE_OPTIONS = [
  { value: "ogiso", label: "Ogiso" },
  { value: "external", label: "External" },
];

const WHO_CAN_ADD_OPTIONS = [
  { value: "admin", label: "Account Admin" },
  { value: "adminStaff", label: "Admin staff" },
  { value: "allStaff", label: "All staff" },
];

// Each chip stands for a family of extensions (Word = doc + docx), mirroring
// the same grouping the Quarterly Report settings page uses for evidence types.
const FILE_TYPE_GROUPS = [
  { label: "PDF", keys: ["pdf"] },
  { label: "Word", keys: ["doc", "docx"] },
  { label: "Excel", keys: ["xls", "xlsx"] },
  { label: "Images", keys: ["jpg", "jpeg", "png"] },
];

const DEFAULT_PROVIDER_SETTINGS = {
  defaultProviderType: "external",
  showExternalProviders: true,
  whoCanAdd: ["admin", "adminStaff"],
  ehrOptions: EHR_OPTIONS,
  requireEhr: true,
  requireNpi: false,
  allowedFileTypes: ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png"],
  defaultFolders: [],
  trackDocumentExpiry: true,
};

const SkypointProviderSettings = () => {
  const { classes, cx } = useStyles();
  const { profile } = useSelector((state) => state?.profile);
  const { providerSettings, providerSettingsLoading } = useSelector((state) => state.skypointProviders);
  const navigate = useNavigate();
  const [section, setSection] = useState("general");

  useEffect(() => {
    getProviderSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settings = providerSettings || DEFAULT_PROVIDER_SETTINGS;
  const onChange = (patch) => updateProviderSettings({ ...settings, ...patch });

  const toggleFrom = (list, field, value) => {
    const current = list || [];
    onChange({ [field]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value] });
  };

  const toggleFileType = (keys) => {
    const current = settings.allowedFileTypes || [];
    const on = keys.some((k) => current.includes(k));
    onChange({ allowedFileTypes: on ? current.filter((k) => !keys.includes(k)) : [...current, ...keys] });
  };

  // "Default folders" is free text typed character by character — debounced
  // the same way the Quarterly Report settings page debounces its title
  // format input, so every keystroke doesn't fire its own PUT /settings.
  const [foldersText, setFoldersText] = useState((providerSettings?.defaultFolders || []).join(", "));
  const initializedFoldersRef = useRef(providerSettings?.defaultFolders != null);
  useEffect(() => {
    if (initializedFoldersRef.current || providerSettings?.defaultFolders == null) return;
    setFoldersText(providerSettings.defaultFolders.join(", "));
    initializedFoldersRef.current = true;
  }, [providerSettings?.defaultFolders]);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const saveTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(saveTimerRef.current), []);
  const handleFoldersChange = (e) => {
    const value = e.target.value;
    setFoldersText(value);
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const defaultFolders = value.split(",").map((s) => s.trim()).filter(Boolean);
      updateProviderSettings({ ...settingsRef.current, defaultFolders });
    }, 500);
  };

  return (
    <Box className={classes.content}>
      <AppNavbar title="Providers" profile={profile} />
      <Box px={20}>
        <button
          type="button"
          className={classes.back}
          onClick={() => navigate("/home/provider-skypoint")}
        >
          <IconArrowLeft size={15} />
          Back
        </button>
        <Text className={classes.heading}>Settings</Text>

        <Box className={classes.layout}>
          <Box className={classes.rail}>
            {SETTINGS_SECTIONS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                className={cx(classes.railItem, {
                  [classes.railItemActive]: section === key,
                })}
                onClick={() => setSection(key)}
              >
                <Icon size={16} stroke={1.9} />
                {label}
              </button>
            ))}
          </Box>

          {providerSettingsLoading && !providerSettings ? (
            <Flex justify="center" align="center" style={{ minHeight: 200 }}>
              <Loader color="violet" />
            </Flex>
          ) : (
          <Box>
            {section === "general" && (
              <Box className={classes.card}>
                <Box className={classes.cardHead}>General</Box>
                <Box className={classes.cardBody}>
                  <Box className={classes.row}>
                    <Box className={classes.rowMain}>
                      <Text className={classes.rowTitle}>Default provider type</Text>
                      <Text className={classes.rowSub}>
                        Which option is preselected when adding a provider.
                      </Text>
                    </Box>
                    <Segmented
                      classes={classes}
                      cx={cx}
                      options={PROVIDER_TYPE_OPTIONS}
                      value={settings.defaultProviderType}
                      onChange={(value) => onChange({ defaultProviderType: value })}
                    />
                  </Box>
                  <Box className={classes.row}>
                    <Box className={classes.rowMain}>
                      <Text className={classes.rowTitle}>Show external providers</Text>
                      <Text className={classes.rowSub}>
                        Include external providers in the provider table.
                      </Text>
                    </Box>
                    <Toggle
                      classes={classes}
                      cx={cx}
                      on={settings.showExternalProviders}
                      onClick={() => onChange({ showExternalProviders: !settings.showExternalProviders })}
                    />
                  </Box>
                  <Box className={classes.row}>
                    <Box className={classes.rowMain}>
                      <Text className={classes.rowTitle}>Who can add providers</Text>
                      <Text className={classes.rowSub}>
                        Staff roles allowed to add or edit providers.
                      </Text>
                    </Box>
                    <ChipRow
                      classes={classes}
                      cx={cx}
                      options={WHO_CAN_ADD_OPTIONS}
                      selected={settings.whoCanAdd || []}
                      onToggle={(v) => toggleFrom(settings.whoCanAdd, "whoCanAdd", v)}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {section === "external" && (
              <Box className={classes.card}>
                <Box className={classes.cardHead}>External Providers</Box>
                <Box className={classes.cardBody}>
                  <Box className={classes.row}>
                    <Box className={classes.rowMain} style={{ maxWidth: "none" }}>
                      <Text className={classes.rowTitle}>EHR options</Text>
                      <Text className={classes.rowSub} style={{ marginBottom: 8 }}>
                        Systems available in the EHR dropdown.
                      </Text>
                      <ChipRow
                        classes={classes}
                        cx={cx}
                        options={EHR_OPTIONS.map((ehr) => ({ value: ehr, label: ehr }))}
                        selected={settings.ehrOptions || []}
                        onToggle={(v) => toggleFrom(settings.ehrOptions, "ehrOptions", v)}
                      />
                    </Box>
                  </Box>
                  <Box className={classes.row}>
                    <Box className={classes.rowMain}>
                      <Text className={classes.rowTitle}>Require an EHR system</Text>
                      <Text className={classes.rowSub}>
                        Make the EHR field mandatory when adding an external provider.
                      </Text>
                    </Box>
                    <Toggle
                      classes={classes}
                      cx={cx}
                      on={settings.requireEhr}
                      onClick={() => onChange({ requireEhr: !settings.requireEhr })}
                    />
                  </Box>
                  <Box className={classes.row}>
                    <Box className={classes.rowMain}>
                      <Text className={classes.rowTitle}>Require an NPI number</Text>
                      <Text className={classes.rowSub}>
                        Ask for the National Provider Identifier on the provider profile.
                      </Text>
                    </Box>
                    <Toggle
                      classes={classes}
                      cx={cx}
                      on={settings.requireNpi}
                      onClick={() => onChange({ requireNpi: !settings.requireNpi })}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {section === "documents" && (
              <Box className={classes.card}>
                <Box className={classes.cardHead}>Documents</Box>
                <Box className={classes.cardBody}>
                  <Box className={classes.row}>
                    <Box className={classes.rowMain} style={{ maxWidth: "none" }}>
                      <Text className={classes.rowTitle}>Allowed file types</Text>
                      <Text className={classes.rowSub} style={{ marginBottom: 8 }}>
                        File formats that can be uploaded to a provider.
                      </Text>
                      <ChipRow
                        classes={classes}
                        cx={cx}
                        options={FILE_TYPE_GROUPS.map((t) => ({ value: t.label, label: t.label }))}
                        selected={FILE_TYPE_GROUPS.filter((t) =>
                          t.keys.some((k) => (settings.allowedFileTypes || []).includes(k))
                        ).map((t) => t.label)}
                        onToggle={(label) => toggleFileType(FILE_TYPE_GROUPS.find((t) => t.label === label).keys)}
                      />
                    </Box>
                  </Box>
                  <Box className={classes.row}>
                    <Box className={classes.rowMain}>
                      <Text className={classes.rowTitle}>Default folders</Text>
                      <Text className={classes.rowSub}>
                        Folders created automatically for each new external provider.
                      </Text>
                    </Box>
                    <input
                      className={classes.input}
                      value={foldersText}
                      onChange={handleFoldersChange}
                      placeholder="Progress note, Facility, Miscellaneous"
                    />
                  </Box>
                  <Box className={classes.row}>
                    <Box className={classes.rowMain}>
                      <Text className={classes.rowTitle}>Track document expiry</Text>
                      <Text className={classes.rowSub}>
                        Show the Active / Expired tabs and flag documents past their
                        expiry date.
                      </Text>
                    </Box>
                    <Toggle
                      classes={classes}
                      cx={cx}
                      on={settings.trackDocumentExpiry}
                      onClick={() => onChange({ trackDocumentExpiry: !settings.trackDocumentExpiry })}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SkypointProviderSettings;
